import React, { Fragment } from 'react';
import { useQuery } from "react-query";
import { Link } from 'react-router-dom';
import qs from 'query-string';
import LazyLoad from 'react-lazyload';

import {
    CardSlider,
    Column,
    ColumnContainer,
    MainButton,
    SectionTitle,
    Paragraph,
    TitleContainer,
    FullImageContainer,
    LoadingContainer,
    ButtonsRow,
    MainContainer
} from "@beacharound/beacharound-ui";
import { getImageCrop } from '@beacharound/beacharound-ui-helpers';

import { getBeaches } from '../../api/beach';
import { useGeolocalization } from '../../contexts/geolocalization';
import { getPlaceholderImage } from '../../helpers/images/getPlaceholderImage';
import PrerenderMeta from '../Prerender/PrerenderMeta';

export default function BeachesSlider({
    title,
    subtitle,
    params,
    sort,
    limit,
    skip,
    place,
    more,
    followParams, // Usato per applicare al link more i parametri della ricerca
    nearest,
    highlight,
    sortByAvailability,
    placeName,
    services,
    accessibilities,
    coordinates,
    maxDistance,
    publicBeaches,
    noMargin,
    darkBg,
    listName
}) {
    const { hasGeolocalization, geoCoordinates } = useGeolocalization();

    let options = {
        limit: limit || 10,
        sort: sort,
        skip: skip,
        place,
        maxDistance: place ? true : maxDistance,
        params: {
            type: publicBeaches ? 'public' : 'private',
            ...params,
        },
    };

    // Costruiamo anche i parametri url da passare alla pagina di more
    let moreUrlParams;

    if (more && followParams) {
        moreUrlParams = qs.stringify({ params: JSON.stringify(options.params) });
    }

    if (
        nearest &&
        hasGeolocalization &&
        geoCoordinates?.latitude &&
        geoCoordinates?.longitude
    ) {
        options = {
            ...options,
            latitude: hasGeolocalization && geoCoordinates?.latitude,
            longitude: hasGeolocalization && geoCoordinates?.longitude,
        }
    }

    if (services) {
        options = {
            ...options,
            services
        }

        if (more && followParams) {
            moreUrlParams = `${moreUrlParams}&${qs.stringify({ services })}`;
        }
    }

    if (accessibilities) {
        options = {
            ...options,
            accessibilities
        }

        if (more && followParams) {
            moreUrlParams = `${moreUrlParams}&${qs.stringify({ accessibilities })}`;
        }
    }

    if (coordinates && coordinates.latitude && coordinates.longitude) {
        options = {
            ...options,
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            maxDistance: true
        }

        if (more && followParams) {
            moreUrlParams = `${moreUrlParams}&${qs.stringify({
                latitude: coordinates.latitude,
                longitude: coordinates.longitude,
            })}`
        }
    }

    if (highlight) {
        options = {
            ...options,
            highlight: true,
            bookable: true
        };

        if (sortByAvailability) {
            options.sortByAvailability = true;
        }
    }

    const { data, isLoading, isFetching } = useQuery(["beaches", options], () => getBeaches({ params: options }), { refetchOnWindowFocus: false });

    const beaches = data?.sort(item => !!placeName && placeName === item?.address?.city ? -1 : 1)?.map((item, index) => {

        const to = `/spiagge/${item.slugCity || item.slug}`;

        let chipLeft, chipRight;

        if (item.bookable === true) {
            chipLeft = [
                { text: `Prenota ora` }
            ];
        }

        if (item.bookable === true && item.hasMap) {
            chipLeft = [
                ...(chipLeft || []),
                { text: "Scegli il tuo posto!", color: "blue" }
            ];
        }

        if (item.bookable === true && !!item.promotion) {
            chipRight = [
                { text: `In promo! -${item.promotion.value}%`, color: "red" }
            ];
        }

        if (item.bookable === true && !!item.hasMenu) {
            chipRight = [
                ...(chipRight || []),
                { text: `Menù disponibile`, color: "green" }
            ];
        }

        if (item.bookable === true && !!item.hasEvents) {
            chipRight = [
                ...(chipRight || []),
                { text: `Eventi in spiaggia`, color: "green" }
            ];
        }

        return {
            image: item.cover?.length > 0 ? getImageCrop(item.cover, "m") : getPlaceholderImage("beaches", item),
            title: item.name,
            description: item.address ? `${item.address?.city || ''}, ${item.address?.district || ''}` : '',
            rating: item.reviews?.average,
            ratingsLength: item.reviews?.count,
            noBorder: darkBg ? false : true,
            imgChipLeft: chipLeft,
            imgChipRight: chipRight,
            component: Link,
            to: listName ? `${to}?listName=${listName}&listIndex=${index + 1}` : to
        };
    });

    const Component = darkBg ? FullImageContainer : 'div';

    if (!beaches || beaches?.length === 0) {
        return <Fragment></Fragment>;
    }

    return (
        <LazyLoad height={200}>
            <LoadingContainer isLoading={isLoading || isFetching}>
                <PrerenderMeta isLoading={isLoading} />
                <Component color="main">
                    <MainContainer>
                        <TitleContainer>
                            <SectionTitle
                                text={(nearest && !hasGeolocalization) ? "Spiagge vicino a te" : title}
                                color={darkBg && "white"}
                            />
                            <Paragraph
                                text={subtitle}
                                color={darkBg && "white"}
                            />
                        </TitleContainer>

                        <ColumnContainer noMargin={noMargin}>
                            <Column>
                                <CardSlider slides={beaches} marginBottom={more} />

                                {more && (
                                    <ButtonsRow fill noMargin>
                                        <MainButton
                                            border
                                            text="Tutte le spiagge"
                                            component={Link}
                                            to={more.url
                                                ? `${more.url}${moreUrlParams ? `?${moreUrlParams}` : ""}`
                                                : publicBeaches
                                                    ? `${more}/spiagge-libere?${moreUrlParams ? `?${moreUrlParams}` : ""}`
                                                    : `${more}/spiagge?${moreUrlParams ? `?${moreUrlParams}` : ""}`
                                            }
                                        />
                                    </ButtonsRow>
                                )}
                            </Column>
                        </ColumnContainer>
                    </MainContainer>
                </Component>
            </LoadingContainer>
        </LazyLoad>
    );
}
